import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DocumentAttachment,
  FormGroup,
  ImageGallery,
  ListItemImageAttachment,
  ListItemVideoAttachment,
  VideoGallery,
} from 'components';
import { MoreDocuments } from 'components/Attachments/DocumentAttachment/styled';
import {
  filesFormGroupMobileStyles,
  filesFormGroupStyles,
} from 'components/FormAttachments/styles';
import { DETAILS_FILE_NAME_LENGTH_LIMIT, PREVIEW_FILE_NAME_LENGTH_LIMIT } from 'constants/index';
import { useModal, useWindowSize } from 'hooks';
import { Attachment } from 'types';
import { hideDocNameMidPart } from 'utils';

import { ContainerW, SectionLabel, SquareImagesRow, SquareThumbnail } from './styled';

interface PostAttachmentsProps {
  attachments: Attachment[];
  postId: string;
  isPreview: boolean;
  goToPostDetails: () => void;
}

export function PostAttachments({
  attachments,
  postId,
  isPreview,
  goToPostDetails,
}: PostAttachmentsProps): ReactElement {
  const { isOpen, handleOpenModal, handleCloseModal } = useModal();
  const {
    isOpen: isOpenVideoGallery,
    handleOpenModal: handleOpenModalVideoGallery,
    handleCloseModal: handleCloseModalVideoGallery,
  } = useModal();
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();

  const imagesList = attachments.filter((attachment) => attachment.type === 'image');
  const videosList = attachments.filter((attachment) => attachment.type === 'video');
  const documentsList = attachments.filter((attachment) => attachment.type === 'document');

  const moreDocuments = documentsList.length > 3 ? documentsList.length - 3 : null;

  const squareThumbnailSize = isMobile ? '64px' : '72px';
  const nameCharLimit = isMobile ? PREVIEW_FILE_NAME_LENGTH_LIMIT : DETAILS_FILE_NAME_LENGTH_LIMIT;
  return (
    <ContainerW>
      {documentsList.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <SectionLabel>{t('common.documentsAvailable')}</SectionLabel>
          <FormGroup sx={isMobile ? filesFormGroupMobileStyles : filesFormGroupStyles}>
            {documentsList.slice(0, 3).map(({ file, id, file_url, file_extension }) => {
              const fileName =
                file.original.length > nameCharLimit
                  ? hideDocNameMidPart({
                      docName: file.original,
                      limit: nameCharLimit,
                    })
                  : file.original;

              return (
                <DocumentAttachment
                  key={id}
                  postId="null"
                  attachmentId={id}
                  fileName={fileName}
                  handleDelete={() => null}
                  hasDownload
                  originalFileName={file.original}
                  originalFileUrl={file_url?.original}
                  file_extension={file_extension}
                  blockDelete
                  isPreview={isPreview}
                />
              );
            })}
            {moreDocuments && moreDocuments > 0 ? (
              <MoreDocuments onClick={goToPostDetails}>
                +{t('common.moreImages', { count: moreDocuments as number })}
              </MoreDocuments>
            ) : null}
          </FormGroup>
        </div>
      )}

      {imagesList.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <SectionLabel>{t('common.imagesAvailable')}</SectionLabel>
          <SquareImagesRow>
            {imagesList.slice(0, 6).map(({ id, file, file_url }, index) => {
              const isLastVisible = index === 5;
              const remaining = imagesList.length > 6 ? imagesList.length - 6 : null;
              return (
                <SquareThumbnail key={id} size={squareThumbnailSize}>
                  <ListItemImageAttachment
                    attachmentId={id}
                    fileName={file.small}
                    fileUrl={file_url?.small}
                    postId={postId}
                    wrapperSx={{
                      width: squareThumbnailSize,
                      height: squareThumbnailSize,
                      borderRadius: '8px',
                      overflow: 'visible',
                      flexShrink: 0,
                    }}
                    onOpenGallery={handleOpenModal}
                    moreImages={isLastVisible ? remaining : null}
                    hasMoreImagesClick={isLastVisible && remaining ? goToPostDetails : undefined}
                  />
                </SquareThumbnail>
              );
            })}
          </SquareImagesRow>
        </div>
      )}

      {videosList.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <SectionLabel>{t('common.videosAvailable')}</SectionLabel>
          <SquareImagesRow>
            {videosList.slice(0, 5).map((video) => (
              <SquareThumbnail key={video.id} size={squareThumbnailSize}>
                <ListItemVideoAttachment
                  video={video}
                  wrapperSx={{
                    width: squareThumbnailSize,
                    height: squareThumbnailSize,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                  onOpenGallery={handleOpenModalVideoGallery}
                />
              </SquareThumbnail>
            ))}
          </SquareImagesRow>
        </div>
      )}

      {isOpen && (
        <ImageGallery
          open={isOpen}
          onClose={handleCloseModal}
          postId={postId}
          imageList={imagesList}
        />
      )}
      {isOpenVideoGallery && (
        <VideoGallery
          open={isOpenVideoGallery}
          onClose={handleCloseModalVideoGallery}
          videoList={videosList}
        />
      )}
    </ContainerW>
  );
}
